import { useLazyQuery } from '@apollo/client/react';
import styled from '@emotion/styled';
import { css } from '@styled-system/css';
import { useSetAtom } from 'jotai';
import React, { useCallback, useEffect, useMemo } from 'react';

import { Box, Button, EntryList, Flex, Grid, Icon, Loader, Text } from '~/components/blocks';
import { Map } from '~/components/partials';
import { GetOrganizationDocument, Practitioner } from '~/graphql/generated/graphql';
import { usePractitionerRole } from '~/hooks/use-practitioner-role';
import { editBaseDrawerState } from '~/state/settings_organization_base/atoms';

import { EditBaseDrawer } from './EditBaseDrawer';
import { formatOrganizationBase } from './formatter';

const Root = styled(Grid)(({ theme }) =>
  css({
    height: '100%',
    gridTemplateColumns: '1fr min-content',
    gridTemplateRows: '1fr',
    gap: theme.space.xxl,
    overflow: 'auto',
  }),
);

const InformationBox = styled(Flex)(({ theme }) =>
  css({
    height: '100%',
    overflow: 'auto',
    flexWrap: 'wrap',
    gap: theme.space.xxl,
  }),
);

const BaseBox = styled(Box)(({ theme }) =>
  css({
    width: '400px',
    [`@media ${theme.mediaQueries.tablet}`]: {},
  }),
);

const MapBox = styled(Box)(({ theme }) =>
  css({
    flex: '1 1 320px',
    [`@media ${theme.mediaQueries.tablet}`]: {},
  }),
);

const ActionBox = styled(Box)();

export const OrganizationBase = () => {
  const [getOrganization, { data, loading }] = useLazyQuery(GetOrganizationDocument);
  const setDrawerState = useSetAtom(editBaseDrawerState);
  const { verifyRole } = usePractitionerRole();

  const organization = useMemo(
    () => (data?.me ? formatOrganizationBase(data.me as Practitioner) : null),
    [data?.me],
  );
  const organizationId = organization ? organization.id : null;

  const handleClick = useCallback(() => {
    if (organizationId) {
      setDrawerState(() => ({ isOpen: true }));
    }
  }, [organizationId, setDrawerState]);

  useEffect(() => {
    getOrganization();
  }, [getOrganization]);

  return (
    <>
      <Root>
        {!organization || loading ? (
          <Loader open inside />
        ) : (
          <>
            <InformationBox>
              <BaseBox>
                <EntryList>
                  <EntryList.Head>店舗コード</EntryList.Head>
                  <EntryList.Body>{organization?.slug}</EntryList.Body>
                </EntryList>
                <EntryList>
                  <EntryList.Head>店舗名</EntryList.Head>
                  <EntryList.Body>
                    <Text wordBreak="break-all">{organization?.name}</Text>
                  </EntryList.Body>
                </EntryList>
                <EntryList>
                  <EntryList.Head>薬局コード</EntryList.Head>
                  <EntryList.Body>{organization?.pharmacyCode || '未設定'}</EntryList.Body>
                </EntryList>
                <EntryList>
                  <EntryList.Head>電話番号</EntryList.Head>
                  <EntryList.Body>{organization?.tel || '未設定'}</EntryList.Body>
                </EntryList>
                <EntryList>
                  <EntryList.Head>FAX</EntryList.Head>
                  <EntryList.Body>{organization?.fax || '未設定'}</EntryList.Body>
                </EntryList>
                <EntryList>
                  <EntryList.Head>郵便番号</EntryList.Head>
                  <EntryList.Body>{organization?.address.postalCode || '未設定'}</EntryList.Body>
                </EntryList>
                <EntryList>
                  <EntryList.Head>都道府県</EntryList.Head>
                  <EntryList.Body>{organization?.address.prefecture || '未設定'}</EntryList.Body>
                </EntryList>
                <EntryList>
                  <EntryList.Head>市区町村</EntryList.Head>
                  <EntryList.Body>{organization?.address.city || '未設定'}</EntryList.Body>
                </EntryList>
                <EntryList>
                  <EntryList.Head>番地・マンション名</EntryList.Head>
                  <EntryList.Body>{organization?.address.line || '未設定'}</EntryList.Body>
                </EntryList>
              </BaseBox>
              <MapBox>
                <Map
                  static
                  width="100%"
                  height={300}
                  latitude={organization?.address.latitude}
                  longitude={organization?.address.longitude}
                />
              </MapBox>
            </InformationBox>
            <ActionBox>
              <Button disabled={!verifyRole('admin')} onClick={handleClick}>
                <Icon icon="edit" size="m" />
                編集
              </Button>
            </ActionBox>
          </>
        )}
      </Root>
      <EditBaseDrawer />
    </>
  );
};
